import {
  useEffect,
  useRef,
  RefObject,
  useState,
  useLayoutEffect,
  Context,
  ContextType,
} from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { v1 as uuidv1 } from 'uuid';
import { datadogRum } from '@datadog/browser-rum';

// @ts-ignore
import { STORE_ROOT, APP_ROOT } from 'utils/constants/urls'

// Aliased Consts because these are imported from a common js module
// @ts-ignore
const STORE_ROOT_URL: string = STORE_ROOT;
// @ts-ignore
const APP_ROOT_URL: string = APP_ROOT;

const formatDate = (date: string) => {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const scrollToId = (id: string) => {
  if (typeof window === `undefined`) return null;
  const headerHeight = 86;
  const el = document.getElementById(id);

  if (!el) return;

  // add polyfill to support smooth scrolling
  smoothscroll.polyfill();

  // scroll to element and make room for header
  window.scrollBy({
    behavior: 'smooth',
    top: el.offsetTop - window.pageYOffset - headerHeight,
  });
};

const useClickOff = (onClose: (e: any) => void) => {
  const wrapperEl = useRef<HTMLDivElement>(null);

  const handleClick = (e: any) => {
    if (!wrapperEl.current || !e.target) {
      return;
    }

    if (!wrapperEl.current.contains(e.target as Node)) {
      onClose(e);
    }
  };

  // Solves the issue of tapping away in iOS to trigger the click event on the document
  useEffect(() => {
    document.body.style.cursor = 'pointer';

    return () => {
      document.body.style.cursor = 'default';
    };
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return wrapperEl;
};

/**
 * Currently an open bug with body-scroll-lock on iPhone specifically iOS 12.x
 * if you touch at the bottom of the page when the browser controls ( bottom bar ) are hidden
 * you are still able to scroll the background
 *
 * when the controls down at the bottom are visible, the body-scroll-lock will work as intended
 *
 * the touchmove event isn't triggered at all down in the area of the bottom toolbar.
 * bug filed: https://bugs.webkit.org/show_bug.cgi?id=189586
 * github issue: https://github.com/willmcpo/body-scroll-lock/issues/82
 * */

const useDisableBodyScroll = (ref?: RefObject<HTMLDivElement>) => {
  const wrapperEl = ref || useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = wrapperEl.current;

    if (node) {
      disableBodyScroll(node, {
        reserveScrollBarGap: true,
      });
    }
    return () => {
      if (node) {
        enableBodyScroll(node);
      }
    };
  }, [wrapperEl]);

  return wrapperEl;
};

const useHasScrollableContent = (parentRef: RefObject<HTMLDivElement>) => {
  const THRESHOLD = 20;
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const handleScroll = (e: any) => {
    if (!e.currentTarget) {
      return;
    }

    const target = e.currentTarget as HTMLDivElement;
    setHasScrollableContent(
      target.scrollHeight - target.scrollTop > target.clientHeight + THRESHOLD,
    );
  };

  useLayoutEffect(() => {
    if (!parentRef.current) {
      return;
    }

    parentRef.current.addEventListener('scroll', handleScroll);

    const { clientHeight, scrollHeight } = parentRef.current;

    if (scrollHeight > clientHeight + THRESHOLD) {
      setHasScrollableContent(true);
    }

    /* eslint-disable-next-line consistent-return */
    return () => {
      if (!parentRef.current) {
        return;
      }

      parentRef.current.removeEventListener('scroll', handleScroll);
    };
  }, [parentRef]);

  return hasScrollableContent;
};

const getLocalStorageItem = (item: string) => {
  const localStorageItem = localStorage.getItem(item);
  if (!localStorageItem) return;

  return localStorageItem;
};

const truncateString = (
  str: string,
  len: number,
  delimiter?: string,
  cap?: string,
) => {
  if (str && str.length > len) {
    const lengthTrim = str.substr(0, len - 1);
    const endCap = cap || '...';
    const delim = delimiter || ' ';
    const finalString =
      lengthTrim.substr(
        0,
        Math.min(lengthTrim.length, lengthTrim.lastIndexOf(delim)),
      ) + endCap;
    return finalString;
  }

  return str;
};

const capitalize = (str: string): string => str[0].toUpperCase() + str.slice(1);

const formatForCurrency = (numberToFormat: number) =>
  new Intl.NumberFormat('en-US', {
    currency: 'USD',
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(numberToFormat);

const generateKey: () => string = () => {
  return uuidv1();
};

const logError = (
  error: string,
  context: object | undefined,
  source: 'custom' | 'network' | 'source' | undefined = 'custom',
): void => {
  datadogRum.addError(error, context, source);
};

const getMembershipEnrollRedirect = (membershipVariant: number, loggedIn: boolean): string => {
  const checkoutUrl = new URL(`${APP_ROOT_URL}/checkout`);
  const loginUrl = new URL(`${STORE_ROOT_URL}/login`);
  if (loggedIn) {
    return checkoutUrl.href;
  } else {
    checkoutUrl.search = `${membershipVariant}:1`;
    loginUrl.search = `my_results_redirect=${checkoutUrl.href}`;

    return loginUrl.href;
  }
}

export {
  capitalize,
  getLocalStorageItem,
  formatDate,
  scrollToId,
  truncateString,
  useClickOff,
  useDisableBodyScroll,
  useHasScrollableContent,
  formatForCurrency,
  generateKey,
  logError,
  getMembershipEnrollRedirect,
};
